import { useTranslation } from 'react-i18next';
import type { OpenTabSummary } from '@gc/shared';

import type { OpenTabSource } from './liveTabs';
import { OpenTabPicker } from './OpenTabPicker';

/**
 * The open-tab picker as a full modal — the Outstanding screen's
 * `VisitorNamePicker` shell (owner request: "like the outstanding bill one"),
 * carrying the richer tab cards: name, where the party is sitting, running
 * total. Backdrop click closes, same as there — a cashier who opened this by
 * accident should not have to find a specific X while somebody waits.
 */
export function OpenTabDialog({
  title,
  value,
  onPick,
  onClose,
  presentOnly = false,
  source = 'open',
}: {
  title: string;
  value: string | null;
  onPick: (tabId: string, tab: OpenTabSummary) => void;
  onClose: () => void;
  presentOnly?: boolean;
  /** See `OpenTabPicker`: `live` for the cafe's Visitor search. */
  source?: OpenTabSource;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-open-tab-dialog
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-24"
      onClick={onClose}
    >
      {/* `short:max-h-none`: on a phone on its side 70vh is ~260px, less than
          the search box plus the list's own 52vh, and `overflow-hidden` cut
          the last names off where no scroll could reach them. Uncapped, the
          panel is its content's height and the backdrop scrolls. */}
      <div
        onClick={(event) => event.stopPropagation()}
        className="max-h-[70vh] w-full max-w-2xl overflow-hidden rounded-card border border-subtle bg-raised shadow-2xl short:max-h-none"
      >
        <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
          <h2 className="text-sm font-semibold text-content">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-xs text-content-secondary hover:bg-hover hover:text-content"
          >
            {t('common.cancel')}
          </button>
        </div>

        <div className="p-3">
          <OpenTabPicker
            autoFocus
            variant="dialog"
            presentOnly={presentOnly}
            source={source}
            value={value}
            onChange={(tabId, tab) => {
              onPick(tabId, tab);
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
}
